import { NavLink, useMatch, Link } from 'react-router-dom'
import {
  LayoutDashboard, Building2, Wallet, Package, Receipt,
  Calculator, FileText, FolderOpen, Settings, X,
  BarChart3, Home, TrendingUp, ChevronRight
} from 'lucide-react'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  label: string
  href: string
  icon: LucideIcon
  exact?: boolean
}

interface NavGroup {
  title?: string
  items: NavItem[]
}

// ─── Navigation Config ────────────────────────────────────────────────────────

const globalNavGroups: NavGroup[] = [
  {
    title: 'IKHTISAR',
    items: [
      { label: 'Dashboard',    href: '/dashboard',   icon: LayoutDashboard, exact: true },
      { label: 'Bisnis Saya',  href: '/businesses',  icon: Building2 },
    ],
  },
]

function getBusinessNavGroups(businessId: string): NavGroup[] {
  const base = `/businesses/${businessId}`
  return [
    {
      title: 'BISNIS AKTIF',
      items: [
        { label: 'Overview',           href: base,                    icon: Home,            exact: true },
        { label: 'Dashboard Keuangan', href: `${base}/dashboard`,     icon: BarChart3 },
        { label: 'Modal & Kepemilikan',href: `${base}/capital`,       icon: Wallet },
        { label: 'Produk & Harga',     href: `${base}/products`,      icon: Package },
        { label: 'Biaya',              href: `${base}/costs`,         icon: Receipt },
        { label: 'Simulasi',           href: `${base}/simulations`,   icon: Calculator },
        { label: 'Data Aktual',        href: `${base}/actuals`,       icon: Receipt },
        { label: 'Ekspor Laporan',     href: `${base}/reports`,       icon: FileText },
        { label: 'Dokumen',            href: `${base}/documents`,     icon: FolderOpen },
      ],
    },
  ]
}

const bottomNavGroup: NavGroup = {
  items: [
    { label: 'Pengaturan', href: '/settings', icon: Settings },
  ],
}

// ─── Sub Components ───────────────────────────────────────────────────────────

function SidebarNavItem({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.href}
      end={item.exact}
      className={({ isActive }) =>
        clsx('nav-item', isActive && 'nav-item-active')
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            size={16}
            className={clsx(
              'shrink-0 transition-colors',
              isActive ? 'text-brand-400' : 'text-slate-500'
            )}
          />
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  )
}

function SidebarNavGroup({ group }: { group: NavGroup }) {
  return (
    <div>
      {group.title && (
        <p className="section-label">{group.title}</p>
      )}
      <div className="space-y-0.5">
        {group.items.map((item) => (
          <SidebarNavItem key={item.href} item={item} />
        ))}
      </div>
    </div>
  )
}

// ─── Main Sidebar ─────────────────────────────────────────────────────────────

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

import { useAuth } from '@/contexts/AuthContext'

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuth()
  // Detect if we're inside a business route
  const businessMatch = useMatch('/businesses/:businessId/*')
  const businessId = businessMatch?.params.businessId

  return (
    <>
      {/* ── Sidebar panel ── */}
      <aside
        className={clsx(
          // Layout
          'fixed top-0 left-0 h-full z-30',
          'w-[240px] flex flex-col',
          // Styling
          'bg-slate-900 border-r border-slate-800',
          // Mobile: slide in/out
          'transition-transform duration-250 ease-out',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* ── Logo / Brand ── */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-slate-800 shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2.5 group" onClick={onClose}>
            {/* Icon */}
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-600 to-brand-500 flex items-center justify-center shadow-lg shadow-brand-900/50 group-hover:shadow-brand-900/70 transition-shadow">
              <TrendingUp size={16} className="text-white" />
            </div>
            {/* Name */}
            <div>
              <p className="text-xs font-bold text-slate-100 leading-none">UMKM Cockpit</p>
              <p className="text-[10px] text-slate-500 leading-none mt-0.5">Business Intelligence</p>
            </div>
          </Link>

          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors"
            aria-label="Tutup menu"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {/* Global nav */}
          {globalNavGroups.map((group, i) => (
            <SidebarNavGroup key={i} group={group} />
          ))}

          {/* Business context nav — only when inside a business route */}
          {businessId && (
            <>
              <div className="gradient-divider my-3" />
              {getBusinessNavGroups(businessId).map((group, i) => (
                <SidebarNavGroup key={`business-${i}`} group={group} />
              ))}
            </>
          )}

          {/* Back to businesses (when in business context) */}
          {businessId && (
            <div className="pt-1">
              <NavLink
                to="/businesses"
                className="nav-item text-slate-500 hover:text-slate-400 text-xs"
              >
                <ChevronRight size={12} className="shrink-0 rotate-180" />
                <span>Kembali ke Daftar Bisnis</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* ── Bottom section ── */}
        <div className="shrink-0 border-t border-slate-800 py-2 px-2">
          <SidebarNavGroup group={bottomNavGroup} />

          {/* User Profile */}
          <div className="mt-2 px-3 py-2.5 rounded-lg flex items-center justify-between gap-2 hover:bg-slate-800 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 border border-slate-600 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-slate-300">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-300 truncate">{user?.name || 'User'}</p>
                <p className="text-[10px] text-slate-500 truncate">{user?.email || 'email@example.com'}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors shrink-0"
              title="Logout"
              aria-label="Logout"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
